/* Xiaomi 2026 · Unwrapped — Dealer year-in-review page
 *
 * Composes all sections from the /api/xiaomi/unwrapped/{retailer_id}
 * payload. Matches the existing Xiaomi shell aesthetic (Fraunces headlines,
 * paper background, tier-coloured hero band, Mi orange accents).
 *
 * Surfaces:
 *   • Hero  — owner first name, outlet, tier, spouse
 *   • Stats — 6 chips (touchpoints / opened / closed / gifts / feedback / media)
 *   • Journey table  — 15-step progress per benefit
 *   • Delivered table — outcome + closed date per benefit
 *   • Photo wall — Cloudinary thumbnails grid (up to 12)
 *   • Feedback quotes — partner's own words
 *   • Thank-you note from Dipali
 *
 * Top-right actions: dealer-selector dropdown, "Download PDF", "ZIP all 284".
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const tok = localStorage.getItem('access_token') || localStorage.getItem('token');
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

export default function XiaomiUnwrapped() {
  const { retailer_id } = useParams();
  const navigate = useNavigate();
  const [dealers, setDealers] = useState([]);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkJob, setBulkJob] = useState(null);

  // Load all dealers for the selector
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/xiaomi/dealers?limit=400`, { headers: authHeaders() });
        const list = Array.isArray(r.data) ? r.data : (r.data?.rows || r.data?.dealers || []);
        setDealers(list);
        // If no retailer_id in URL, default to the first
        if (!retailer_id && list.length > 0) {
          navigate(`/xiaomi/unwrapped/${list[0].retailer_id}`, { replace: true });
        }
      } catch (e) {
        toast.error('Failed to load dealer list');
      }
    })();
  }, []);

  // Load story whenever retailer_id changes
  useEffect(() => {
    if (!retailer_id) return;
    setLoading(true);
    setStory(null);
    axios
      .get(`${API}/xiaomi/unwrapped/${retailer_id}`, { headers: authHeaders() })
      .then((r) => setStory(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load story'))
      .finally(() => setLoading(false));
  }, [retailer_id]);

  const onDownloadPdf = async () => {
    try {
      const r = await axios.get(`${API}/xiaomi/unwrapped/${retailer_id}/pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Xiaomi_Unwrapped_2026_${retailer_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF download failed');
    }
  };

  const onStartBulk = async () => {
    try {
      const r = await axios.post(`${API}/xiaomi/unwrapped/bulk-zip`, {}, { headers: authHeaders() });
      setBulkJob({ job_id: r.data.job_id, status: 'queued', total: r.data.total, progress: 0 });
      toast.success(`Queued ZIP-all job for ${r.data.total} dealers`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to queue bulk job');
    }
  };

  // Poll the bulk job until done
  useEffect(() => {
    if (!bulkJob?.job_id || bulkJob.status === 'done' || bulkJob.status === 'failed') return;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/xiaomi/unwrapped/bulk-zip/${bulkJob.job_id}`, {
          headers: authHeaders(),
        });
        setBulkJob(r.data);
        if (r.data.status === 'done') {
          toast.success(`ZIP ready · ${r.data.succeeded}/${r.data.total} PDFs · ${(r.data.size_bytes / 1024 / 1024).toFixed(1)} MB`);
        } else if (r.data.status === 'failed') {
          toast.error(`Bulk job failed: ${r.data.error || 'unknown'}`);
        }
      } catch (e) {
        // ignore transient
      }
    }, 3000);
    return () => clearInterval(id);
  }, [bulkJob?.job_id, bulkJob?.status]);

  const onDownloadZip = () => {
    if (!bulkJob?.job_id) return;
    const url = `${API}/xiaomi/unwrapped/bulk-zip/${bulkJob.job_id}/download`;
    // Use fetch-blob so Authorization header is sent
    axios
      .get(url, { headers: authHeaders(), responseType: 'blob' })
      .then((r) => {
        const blobUrl = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = bulkJob.file || 'xiaomi_unwrapped.zip';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error('Download failed'));
  };

  const sortedDealers = useMemo(
    () =>
      [...dealers].sort((a, b) => String(a.outlet_name || '').localeCompare(String(b.outlet_name || ''))),
    [dealers],
  );

  return (
    <div className="space-y-6" data-testid="xiaomi-unwrapped-page">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif', color: '#15110d' }}>
            Xiaomi <span style={{ color: '#ff6900' }}>Unwrapped</span> · 2026
          </h1>
          <p className="text-sm text-soft mt-1">Personalised year-in-review for every Platinum Partner.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={retailer_id || ''}
            onChange={(e) => navigate(`/xiaomi/unwrapped/${e.target.value}`)}
            className="border border-line rounded-lg px-3 py-2 text-sm bg-panel min-w-[280px]"
            data-testid="unwrapped-dealer-select"
          >
            {sortedDealers.map((d) => (
              <option key={d.retailer_id} value={d.retailer_id}>
                {d.outlet_name} · {d.owner_name} · {d.tier}
              </option>
            ))}
          </select>
          <button
            onClick={onDownloadPdf}
            disabled={!story || loading}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-line bg-panel hover:bg-[#faf6ef] disabled:opacity-40"
            data-testid="unwrapped-download-pdf"
          >
            ↓ Download PDF
          </button>
          <button
            onClick={onStartBulk}
            disabled={!!bulkJob && bulkJob.status !== 'done' && bulkJob.status !== 'failed'}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ background: '#ff6900' }}
            data-testid="unwrapped-bulk-zip"
          >
            📦 ZIP all 284
          </button>
        </div>
      </div>

      {/* Bulk job banner */}
      {bulkJob && (
        <div
          className="rounded-lg p-3 border text-sm flex items-center justify-between gap-3"
          style={{ background: '#fffdf9', borderColor: '#e6dccd' }}
          data-testid="unwrapped-bulk-status"
        >
          <div>
            Bulk job <code className="font-mono text-xs bg-[#f3ebd9] px-1.5 py-0.5 rounded">{bulkJob.job_id}</code> · status:{' '}
            <b>{bulkJob.status}</b>
            {bulkJob.total ? (
              <> · {bulkJob.progress || 0} / {bulkJob.total}</>
            ) : null}
            {bulkJob.failed > 0 && <> · failed {bulkJob.failed}</>}
          </div>
          {bulkJob.status === 'done' && (
            <button
              onClick={onDownloadZip}
              className="px-3 py-1.5 rounded-md bg-[#15110d] text-white text-xs"
              data-testid="unwrapped-bulk-download"
            >
              ↓ Download ZIP ({(bulkJob.size_bytes / 1024 / 1024).toFixed(1)} MB)
            </button>
          )}
        </div>
      )}

      {loading && <div className="text-sm text-soft" data-testid="unwrapped-loading">Loading story…</div>}

      {story && <UnwrappedStory story={story} />}
    </div>
  );
}

function UnwrappedStory({ story }) {
  const dealer = story.dealer || {};
  const accent = story.tier_accent || '#15110d';
  const stats = story.stats || {};
  const ownerFirst = (dealer.owner_first_name || dealer.owner_name || 'Partner').split(' ')[0];
  const spouse = dealer.spouse_name && String(dealer.spouse_name).toLowerCase() !== 'none' ? dealer.spouse_name : null;

  return (
    <>
      {/* HERO */}
      <section
        className="rounded-2xl text-white p-10"
        style={{ background: accent }}
        data-testid="unwrapped-hero"
      >
        <div className="text-[10px] tracking-[3px] opacity-80 mb-5">XIAOMI ONE · CONCIERGE · 2026 UNWRAPPED</div>
        <h2 className="text-6xl font-bold leading-none mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
          {ownerFirst}
        </h2>
        <div className="text-base opacity-95">{dealer.outlet_name}</div>
        <div className="text-xs opacity-80 mt-2 tracking-wide">
          {dealer.tier} · {dealer.city}, {dealer.state}
        </div>
        {spouse && <div className="text-sm italic mt-4 opacity-95">with {spouse}</div>}
      </section>

      {/* STATS */}
      <SectionTitle accent={accent} title="Your year, by the numbers" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="unwrapped-stats">
        <StatChip accent={accent} label="Touchpoints" value={stats.touchpoints} />
        <StatChip accent={accent} label="Benefits opened" value={stats.benefits_opened} />
        <StatChip accent={accent} label="Benefits closed" value={stats.benefits_closed} />
        <StatChip accent={accent} label="Gifts received" value={stats.gifts_received} />
        <StatChip accent={accent} label="Feedback shared" value={stats.feedback_received} />
        <StatChip accent={accent} label="Photos & videos" value={stats.media_uploaded} />
      </div>

      {/* JOURNEYS */}
      {story.journeys?.length > 0 && (
        <>
          <SectionTitle accent={accent} title="15-step Partner Journey" />
          <DataTable
            testId="unwrapped-journeys"
            cols={[
              { k: 'benefit_name', label: 'Benefit' },
              { k: 'month_ym', label: 'Month' },
              { k: 'progress', label: 'Steps', align: 'right', render: (j) => `${j.steps_done} / ${j.steps_total}` },
              { k: 'last_updated', label: 'Last update' },
            ]}
            rows={story.journeys}
          />
        </>
      )}

      {/* DELIVERED */}
      {story.delivered?.length > 0 && (
        <>
          <SectionTitle accent={accent} title="Benefits delivered" />
          <DataTable
            testId="unwrapped-delivered"
            cols={[
              { k: 'benefit_name', label: 'Benefit' },
              { k: 'month_ym', label: 'Month' },
              { k: 'outcome', label: 'Outcome' },
              { k: 'closed_at', label: 'Closed' },
            ]}
            rows={story.delivered}
          />
        </>
      )}

      {/* GIFTS */}
      {story.gifts?.length > 0 && (
        <>
          <SectionTitle accent={accent} title="Gifts" />
          <DataTable
            testId="unwrapped-gifts"
            cols={[
              { k: 'catalogue_label', label: 'Gift' },
              { k: 'month_ym', label: 'Month' },
              { k: 'stage', label: 'Stage' },
              { k: 'supplier_name', label: 'Supplier' },
              { k: 'delivered_on', label: 'Delivered' },
            ]}
            rows={story.gifts}
          />
        </>
      )}

      {/* PHOTO WALL */}
      {story.photo_wall?.length > 0 && (
        <>
          <SectionTitle accent={accent} title="Photo wall" />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="unwrapped-photos">
            {story.photo_wall.map((p, i) => (
              <a
                key={i}
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className="block bg-panel border border-line rounded-lg overflow-hidden hover:shadow-md transition"
              >
                {p.kind === 'video' ? (
                  <video src={p.url} className="w-full h-32 object-cover" />
                ) : (
                  <img src={p.thumb || p.url} alt="" className="w-full h-32 object-cover" />
                )}
                <div className="text-[10px] uppercase tracking-wider text-soft px-2 py-1.5 truncate">{p.benefit}</div>
              </a>
            ))}
          </div>
        </>
      )}

      {/* FEEDBACK */}
      {story.feedback_quotes?.length > 0 && (
        <>
          <SectionTitle accent={accent} title="In your own words" />
          <div className="space-y-2" data-testid="unwrapped-feedback">
            {story.feedback_quotes.map((q, i) => (
              <blockquote
                key={i}
                className="bg-panel border-l-4 p-4 rounded-r-lg"
                style={{ borderLeftColor: accent }}
              >
                <div className="text-base" style={{ fontFamily: 'Fraunces, serif' }}>
                  "{q.text}"
                </div>
                <div className="text-xs text-soft mt-2">
                  — after {q.benefit} · {q.month_ym}
                </div>
              </blockquote>
            ))}
          </div>
        </>
      )}

      {/* THANK YOU */}
      <SectionTitle accent={accent} title="From all of us" />
      <div className="bg-panel border border-line rounded-lg p-6 text-sm leading-relaxed">
        <p>
          {ownerFirst}, thank you for the trust you placed in the Xiaomi Platinum Partners 2026 programme. Every benefit on
          these pages is a small token of how much your partnership means to us. Here's to an even more remarkable 2027.
        </p>
        <p className="mt-4">
          With warmth,
          <br />
          <strong>Dipali Sikand</strong>
          <br />
          Founder, Les Concierges
        </p>
      </div>

      <div className="text-[10px] text-soft uppercase tracking-wider pt-4">
        Generated by ScaleBoard · {new Date(story.generated_at).toLocaleDateString()} · Internal use only · Retailer ID {dealer.retailer_id}
      </div>
    </>
  );
}

function SectionTitle({ accent, title }) {
  return (
    <div
      className="font-semibold text-lg pl-3 mt-6 mb-2"
      style={{ fontFamily: 'Fraunces, serif', borderLeft: `3px solid ${accent}` }}
    >
      {title}
    </div>
  );
}

function StatChip({ accent, label, value }) {
  return (
    <div className="bg-panel border border-line rounded-xl p-4">
      <div className="text-3xl font-bold leading-none" style={{ color: accent, fontFamily: 'Fraunces, serif' }}>
        {value ?? 0}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-soft mt-2">{label}</div>
    </div>
  );
}

function DataTable({ cols, rows, testId }) {
  return (
    <div className="overflow-x-auto border border-line rounded-lg" data-testid={testId}>
      <table className="w-full text-sm">
        <thead style={{ background: '#f3ebd9' }}>
          <tr>
            {cols.map((c) => (
              <th
                key={c.k}
                className={`px-3 py-2 text-[10px] uppercase tracking-wider text-soft font-semibold ${
                  c.align === 'right' ? 'text-right' : 'text-left'
                }`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-line">
              {cols.map((c) => (
                <td key={c.k} className={`px-3 py-2 ${c.align === 'right' ? 'text-right' : ''}`}>
                  {c.render ? c.render(r) : r[c.k] || '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
